import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import AuthGuard from '@/components/layout/AuthGuard';
import DashboardLayout from '@/components/layout/DashboardLayout';

// Loading fallback
function PageLoading() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', minHeight: 400, color: '#888', fontSize: 14,
    }}>
      Loading...
    </div>
  );
}

// Lazy wrapper for code splitting
function L(factory: () => Promise<{ default: React.ComponentType }>) {
  const Component = lazy(factory);
  return (
    <Suspense fallback={<PageLoading />}>
      <Component />
    </Suspense>
  );
}

// --- Auth Pages ---
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const MfaPage = lazy(() => import('@/pages/auth/MfaPage'));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));
const InvitePage = lazy(() => import('@/pages/auth/InvitePage'));
const PartnerInvitePage = lazy(() => import('@/pages/auth/PartnerInvitePage'));

// --- Marketing Pages ---
const HomePage = lazy(() => import('@/pages/marketing/HomePage'));
const PricingPage = lazy(() => import('@/pages/marketing/PricingPage'));
const ContactPage = lazy(() => import('@/pages/marketing/ContactPage'));
const WaitlistPage = lazy(() => import('@/pages/marketing/WaitlistPage'));
const SolutionsPage = lazy(() => import('@/pages/marketing/SolutionsPage'));
const ResourcesPage = lazy(() => import('@/pages/marketing/ResourcesPage'));
const CustomersPage = lazy(() => import('@/pages/marketing/CustomersPage'));
const BlogPage = lazy(() => import('@/pages/marketing/blog/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/marketing/blog/BlogPostPage'));
const DocsPage = lazy(() => import('@/pages/marketing/docs/DocsPage'));
const GettingStartedPage = lazy(() => import('@/pages/marketing/docs/GettingStartedPage'));
const IdentitiesDocPage = lazy(() => import('@/pages/marketing/docs/IdentitiesDocPage'));
const CredentialsDocPage = lazy(() => import('@/pages/marketing/docs/CredentialsDocPage'));
const PoliciesDocPage = lazy(() => import('@/pages/marketing/docs/PoliciesDocPage'));
const JitAccessDocPage = lazy(() => import('@/pages/marketing/docs/JitAccessDocPage'));
const AiAgentsDocPage = lazy(() => import('@/pages/marketing/docs/AiAgentsDocPage'));
const CloudIntegrationsDocPage = lazy(() => import('@/pages/marketing/docs/CloudIntegrationsDocPage'));
const CicdIntegrationsDocPage = lazy(() => import('@/pages/marketing/docs/CicdIntegrationsDocPage'));
const SdkDocPage = lazy(() => import('@/pages/marketing/docs/SdkDocPage'));
const PrivacyPage = lazy(() => import('@/pages/marketing/legal/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/marketing/legal/TermsPage'));
const SecurityPage = lazy(() => import('@/pages/marketing/legal/SecurityPage'));
const AboutPage = lazy(() => import('@/pages/marketing/company/AboutPage'));
const CareersPage = lazy(() => import('@/pages/marketing/company/CareersPage'));
const PartnersPage = lazy(() => import('@/pages/marketing/partners/PartnersPage'));
const MspPage = lazy(() => import('@/pages/marketing/partners/MspPage'));
const ResellerPage = lazy(() => import('@/pages/marketing/partners/ResellerPage'));
const TechnologyPage = lazy(() => import('@/pages/marketing/partners/TechnologyPage'));
const ReferralPage = lazy(() => import('@/pages/marketing/partners/ReferralPage'));
const PartnerApplyPage = lazy(() => import('@/pages/marketing/partners/PartnerApplyPage'));
const AiAgentsPage = lazy(() => import('@/pages/marketing/solutions/AiAgentsPage'));
const AiAgentManagementPage = lazy(() => import('@/pages/marketing/solutions/AiAgentManagementPage'));
const ApiSecurityPage = lazy(() => import('@/pages/marketing/solutions/ApiSecurityPage'));
const ApiKeysPage = lazy(() => import('@/pages/marketing/solutions/ApiKeysPage'));
const MarketingCompliancePage = lazy(() => import('@/pages/marketing/solutions/CompliancePage'));
const MarketingJitAccessPage = lazy(() => import('@/pages/marketing/solutions/JitAccessPage'));
const ServiceAccountsPage = lazy(() => import('@/pages/marketing/solutions/ServiceAccountsPage'));
const CiCdPage = lazy(() => import('@/pages/marketing/solutions/CiCdPage'));

// --- Dashboard Pages ---
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const AnalyticsPage = lazy(() => import('@/pages/dashboard/AnalyticsPage'));
const IdentitiesPage = lazy(() => import('@/pages/dashboard/IdentitiesPage'));
const IdentityNewPage = lazy(() => import('@/pages/dashboard/IdentityNewPage'));
const IdentityDetailPage = lazy(() => import('@/pages/dashboard/IdentityDetailPage'));
const IdentityGroupsPage = lazy(() => import('@/pages/dashboard/IdentityGroupsPage'));
const IdentityGroupDetailPage = lazy(() => import('@/pages/dashboard/IdentityGroupDetailPage'));
const IdentityRelationshipsPage = lazy(() => import('@/pages/dashboard/IdentityRelationshipsPage'));
const IdentityCorrelationPage = lazy(() => import('@/pages/dashboard/IdentityCorrelationPage'));
const CredentialsPage = lazy(() => import('@/pages/dashboard/CredentialsPage'));
const CredentialRotationPage = lazy(() => import('@/pages/dashboard/CredentialRotationPage'));
const CredentialSyncPage = lazy(() => import('@/pages/dashboard/CredentialSyncPage'));
const CredentialHealthPage = lazy(() => import('@/pages/dashboard/CredentialHealthPage'));
const OAuthGovernancePage = lazy(() => import('@/pages/dashboard/OAuthGovernancePage'));
const DiscoveryPage = lazy(() => import('@/pages/dashboard/DiscoveryPage'));
const DiscoveryConnectionNewPage = lazy(() => import('@/pages/dashboard/DiscoveryConnectionNewPage'));
const DashPoliciesPage = lazy(() => import('@/pages/dashboard/PoliciesPage'));
const PolicyNewPage = lazy(() => import('@/pages/dashboard/PolicyNewPage'));
const PolicyDetailPage = lazy(() => import('@/pages/dashboard/PolicyDetailPage'));
const JitAccessPage = lazy(() => import('@/pages/dashboard/JitAccessPage'));
const AttestationPage = lazy(() => import('@/pages/dashboard/AttestationPage'));
const AttestationNewPage = lazy(() => import('@/pages/dashboard/AttestationNewPage'));
const BlastRadiusPage = lazy(() => import('@/pages/dashboard/BlastRadiusPage'));
const TeamsPage = lazy(() => import('@/pages/dashboard/TeamsPage'));
const TeamDetailPage = lazy(() => import('@/pages/dashboard/TeamDetailPage'));
const WebhooksPage = lazy(() => import('@/pages/dashboard/WebhooksPage'));
const WebhookNewPage = lazy(() => import('@/pages/dashboard/WebhookNewPage'));
const WebhookDetailPage = lazy(() => import('@/pages/dashboard/WebhookDetailPage'));
const AlertsPage = lazy(() => import('@/pages/dashboard/AlertsPage'));
const AlertRulesPage = lazy(() => import('@/pages/dashboard/AlertRulesPage'));
const AuditLogsPage = lazy(() => import('@/pages/dashboard/AuditLogsPage'));
const BehaviorComparisonPage = lazy(() => import('@/pages/dashboard/BehaviorComparisonPage'));
const CompliancePage = lazy(() => import('@/pages/dashboard/CompliancePage'));
const IntegrationsStatusPage = lazy(() => import('@/pages/dashboard/IntegrationsStatusPage'));
const ProfilePage = lazy(() => import('@/pages/dashboard/ProfilePage'));

// --- Agent Pages ---
const AgentsPage = lazy(() => import('@/pages/agents/AgentsPage'));
const AgentNewPage = lazy(() => import('@/pages/agents/AgentNewPage'));
const AgentDetailPage = lazy(() => import('@/pages/agents/AgentDetailPage'));
const CommandCenterPage = lazy(() => import('@/pages/agents/CommandCenterPage'));
const AnomaliesPage = lazy(() => import('@/pages/agents/AnomaliesPage'));
const AgentPoliciesPage = lazy(() => import('@/pages/agents/AgentPoliciesPage'));
const AgentPolicyNewPage = lazy(() => import('@/pages/agents/AgentPolicyNewPage'));
const AgentPolicyDetailPage = lazy(() => import('@/pages/agents/AgentPolicyDetailPage'));
const ApprovalsPage = lazy(() => import('@/pages/agents/ApprovalsPage'));
const ApprovalDetailPage = lazy(() => import('@/pages/agents/ApprovalDetailPage'));
const AgentDiscoveryPage = lazy(() => import('@/pages/agents/AgentDiscoveryPage'));
const AgentDiscoveryDetailPage = lazy(() => import('@/pages/agents/AgentDiscoveryDetailPage'));
const McpServersPage = lazy(() => import('@/pages/agents/McpServersPage'));
const McpServerDetailPage = lazy(() => import('@/pages/agents/McpServerDetailPage'));
const PlatformsPage = lazy(() => import('@/pages/agents/PlatformsPage'));
const PlatformNewPage = lazy(() => import('@/pages/agents/PlatformNewPage'));
const PlatformDetailPage = lazy(() => import('@/pages/agents/PlatformDetailPage'));
const PlatformOffboardPage = lazy(() => import('@/pages/agents/PlatformOffboardPage'));

// --- Settings Pages ---
const SettingsPage = lazy(() => import('@/pages/dashboard/SettingsPage'));
const SettingsSecurityPage = lazy(() => import('@/pages/dashboard/SettingsSecurityPage'));
const SettingsIntegrationsPage = lazy(() => import('@/pages/dashboard/SettingsIntegrationsPage'));
const SettingsNotificationsPage = lazy(() => import('@/pages/dashboard/SettingsNotificationsPage'));
const SettingsAiSecurityPage = lazy(() => import('@/pages/dashboard/SettingsAiSecurityPage'));
const SettingsAiFrameworksPage = lazy(() => import('@/pages/dashboard/SettingsAiFrameworksPage'));

// --- Management Portal ---
const MgtDashboardPage = lazy(() => import('@/pages/mgt/MgtDashboardPage'));
const MgtClientsPage = lazy(() => import('@/pages/mgt/MgtClientsPage'));
const MgtClientNewPage = lazy(() => import('@/pages/mgt/MgtClientNewPage'));
const MgtClientDetailPage = lazy(() => import('@/pages/mgt/MgtClientDetailPage'));
const MgtPartnersPage = lazy(() => import('@/pages/mgt/MgtPartnersPage'));
const MgtPartnerNewPage = lazy(() => import('@/pages/mgt/MgtPartnerNewPage'));
const MgtPartnerDetailPage = lazy(() => import('@/pages/mgt/MgtPartnerDetailPage'));
const MgtAiModelsPage = lazy(() => import('@/pages/mgt/MgtAiModelsPage'));
const MgtAiModelNewPage = lazy(() => import('@/pages/mgt/MgtAiModelNewPage'));
const MgtAiModelDetailPage = lazy(() => import('@/pages/mgt/MgtAiModelDetailPage'));
const MgtPlansPage = lazy(() => import('@/pages/mgt/MgtPlansPage'));
const MgtSecurityPage = lazy(() => import('@/pages/mgt/MgtSecurityPage'));

// --- MSP Portal ---
const MspDashboardPage = lazy(() => import('@/pages/msp/MspDashboardPage'));
const MspClientsPage = lazy(() => import('@/pages/msp/MspClientsPage'));
const MspClientDetailPage = lazy(() => import('@/pages/msp/MspClientDetailPage'));
const MspAddClientPage = lazy(() => import('@/pages/msp/MspAddClientPage'));
const MspTeamPage = lazy(() => import('@/pages/msp/MspTeamPage'));
const MspBillingPage = lazy(() => import('@/pages/msp/MspBillingPage'));
const MspSettingsPage = lazy(() => import('@/pages/msp/MspSettingsPage'));
const MspSupportPage = lazy(() => import('@/pages/msp/MspSupportPage'));

// --- Partner Portal ---
const PartnerDashboardPage = lazy(() => import('@/pages/partner/PartnerDashboardPage'));
const PartnerPayoutsPage = lazy(() => import('@/pages/partner/PartnerPayoutsPage'));
const PartnerResourcesPage = lazy(() => import('@/pages/partner/PartnerResourcesPage'));

// Layout wrappers for portals
function MgtLayout() {
  return <Outlet />;
}
function MspLayout() {
  return <Outlet />;
}
function PartnerLayout() {
  return <Outlet />;
}
function MarketingLayout() {
  return <Outlet />;
}

function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Auth routes */}
        <Route path="/login" element={L(() => import('@/pages/auth/LoginPage'))} />
        <Route path="/mfa" element={L(() => import('@/pages/auth/MfaPage'))} />
        <Route path="/accept-invite" element={L(() => import('@/pages/auth/AcceptInvitePage'))} />
        <Route path="/invite" element={L(() => import('@/pages/auth/InvitePage'))} />
        <Route path="/invite/partner/:token" element={L(() => import('@/pages/auth/PartnerInvitePage'))} />
        <Route path="/api/auth/callback/verify" element={L(() => import('@/pages/auth/VerifyCallbackPage'))} />

        {/* Marketing routes */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={L(() => import('@/pages/marketing/HomePage'))} />
          <Route path="/pricing" element={L(() => import('@/pages/marketing/PricingPage'))} />
          <Route path="/contact" element={L(() => import('@/pages/marketing/ContactPage'))} />
          <Route path="/waitlist" element={L(() => import('@/pages/marketing/WaitlistPage'))} />
          <Route path="/solutions" element={L(() => import('@/pages/marketing/SolutionsPage'))} />
          <Route path="/solutions/ai-agents" element={L(() => import('@/pages/marketing/solutions/AiAgentsPage'))} />
          <Route path="/solutions/ai-agent-management" element={L(() => import('@/pages/marketing/solutions/AiAgentManagementPage'))} />
          <Route path="/solutions/api-security" element={L(() => import('@/pages/marketing/solutions/ApiSecurityPage'))} />
          <Route path="/solutions/api-keys" element={L(() => import('@/pages/marketing/solutions/ApiKeysPage'))} />
          <Route path="/solutions/compliance" element={L(() => import('@/pages/marketing/solutions/CompliancePage'))} />
          <Route path="/solutions/jit-access" element={L(() => import('@/pages/marketing/solutions/JitAccessPage'))} />
          <Route path="/solutions/service-accounts" element={L(() => import('@/pages/marketing/solutions/ServiceAccountsPage'))} />
          <Route path="/solutions/ci-cd" element={L(() => import('@/pages/marketing/solutions/CiCdPage'))} />
          <Route path="/resources" element={L(() => import('@/pages/marketing/ResourcesPage'))} />
          <Route path="/resources/blog" element={L(() => import('@/pages/marketing/blog/BlogPage'))} />
          <Route path="/resources/blog/:slug" element={L(() => import('@/pages/marketing/blog/BlogPostPage'))} />
          <Route path="/customers" element={L(() => import('@/pages/marketing/CustomersPage'))} />
          <Route path="/docs" element={L(() => import('@/pages/marketing/docs/DocsPage'))} />
          <Route path="/docs/getting-started" element={L(() => import('@/pages/marketing/docs/GettingStartedPage'))} />
          <Route path="/docs/identities" element={L(() => import('@/pages/marketing/docs/IdentitiesDocPage'))} />
          <Route path="/docs/credentials" element={L(() => import('@/pages/marketing/docs/CredentialsDocPage'))} />
          <Route path="/docs/policies" element={L(() => import('@/pages/marketing/docs/PoliciesDocPage'))} />
          <Route path="/docs/jit-access" element={L(() => import('@/pages/marketing/docs/JitAccessDocPage'))} />
          <Route path="/docs/ai-agents" element={L(() => import('@/pages/marketing/docs/AiAgentsDocPage'))} />
          <Route path="/docs/cloud-integrations" element={L(() => import('@/pages/marketing/docs/CloudIntegrationsDocPage'))} />
          <Route path="/docs/ci-cd-integrations" element={L(() => import('@/pages/marketing/docs/CicdIntegrationsDocPage'))} />
          <Route path="/docs/sdk" element={L(() => import('@/pages/marketing/docs/SdkDocPage'))} />
          <Route path="/legal/privacy" element={L(() => import('@/pages/marketing/legal/PrivacyPage'))} />
          <Route path="/legal/terms" element={L(() => import('@/pages/marketing/legal/TermsPage'))} />
          <Route path="/legal/security" element={L(() => import('@/pages/marketing/legal/SecurityPage'))} />
          <Route path="/company/about" element={L(() => import('@/pages/marketing/company/AboutPage'))} />
          <Route path="/company/careers" element={L(() => import('@/pages/marketing/company/CareersPage'))} />
          <Route path="/partners" element={L(() => import('@/pages/marketing/partners/PartnersPage'))} />
          <Route path="/partners/msp" element={L(() => import('@/pages/marketing/partners/MspPage'))} />
          <Route path="/partners/reseller" element={L(() => import('@/pages/marketing/partners/ResellerPage'))} />
          <Route path="/partners/technology" element={L(() => import('@/pages/marketing/partners/TechnologyPage'))} />
          <Route path="/partners/referral" element={L(() => import('@/pages/marketing/partners/ReferralPage'))} />
          <Route path="/partners/apply" element={L(() => import('@/pages/marketing/partners/PartnerApplyPage'))} />
        </Route>

        {/* Dashboard routes (auth-protected) */}
        <Route element={<AuthGuard><DashboardLayout /></AuthGuard>}>
          <Route path="/dashboard" element={L(() => import('@/pages/dashboard/DashboardPage'))} />
          <Route path="/analytics" element={L(() => import('@/pages/dashboard/AnalyticsPage'))} />

          {/* Core Management */}
          <Route path="/identities" element={L(() => import('@/pages/dashboard/IdentitiesPage'))} />
          <Route path="/identities/new" element={L(() => import('@/pages/dashboard/IdentityNewPage'))} />
          <Route path="/identities/:id" element={L(() => import('@/pages/dashboard/IdentityDetailPage'))} />
          <Route path="/identity-groups" element={L(() => import('@/pages/dashboard/IdentityGroupsPage'))} />
          <Route path="/identity-groups/:id" element={L(() => import('@/pages/dashboard/IdentityGroupDetailPage'))} />
          <Route path="/identity-relationships" element={L(() => import('@/pages/dashboard/IdentityRelationshipsPage'))} />
          <Route path="/identity-correlation" element={L(() => import('@/pages/dashboard/IdentityCorrelationPage'))} />
          <Route path="/credentials" element={L(() => import('@/pages/dashboard/CredentialsPage'))} />
          <Route path="/credentials/:id/rotation" element={L(() => import('@/pages/dashboard/CredentialRotationPage'))} />
          <Route path="/credentials/:id/sync" element={L(() => import('@/pages/dashboard/CredentialSyncPage'))} />
          <Route path="/credential-health" element={L(() => import('@/pages/dashboard/CredentialHealthPage'))} />
          <Route path="/oauth-governance" element={L(() => import('@/pages/dashboard/OAuthGovernancePage'))} />
          <Route path="/discovery" element={L(() => import('@/pages/dashboard/DiscoveryPage'))} />
          <Route path="/discovery/connections/new" element={L(() => import('@/pages/dashboard/DiscoveryConnectionNewPage'))} />
          <Route path="/policies" element={L(() => import('@/pages/dashboard/PoliciesPage'))} />
          <Route path="/policies/new" element={L(() => import('@/pages/dashboard/PolicyNewPage'))} />
          <Route path="/policies/:id" element={L(() => import('@/pages/dashboard/PolicyDetailPage'))} />
          <Route path="/jit-access" element={L(() => import('@/pages/dashboard/JitAccessPage'))} />
          <Route path="/attestation" element={L(() => import('@/pages/dashboard/AttestationPage'))} />
          <Route path="/attestation/new" element={L(() => import('@/pages/dashboard/AttestationNewPage'))} />
          <Route path="/blast-radius" element={L(() => import('@/pages/dashboard/BlastRadiusPage'))} />
          <Route path="/teams" element={L(() => import('@/pages/dashboard/TeamsPage'))} />
          <Route path="/teams/:id" element={L(() => import('@/pages/dashboard/TeamDetailPage'))} />
          <Route path="/webhooks" element={L(() => import('@/pages/dashboard/WebhooksPage'))} />
          <Route path="/webhooks/new" element={L(() => import('@/pages/dashboard/WebhookNewPage'))} />
          <Route path="/webhooks/:id" element={L(() => import('@/pages/dashboard/WebhookDetailPage'))} />
          <Route path="/alerts" element={L(() => import('@/pages/dashboard/AlertsPage'))} />
          <Route path="/alerts/rules" element={L(() => import('@/pages/dashboard/AlertRulesPage'))} />
          <Route path="/audit-logs" element={L(() => import('@/pages/dashboard/AuditLogsPage'))} />
          <Route path="/behavior-comparison" element={L(() => import('@/pages/dashboard/BehaviorComparisonPage'))} />
          <Route path="/compliance" element={L(() => import('@/pages/dashboard/CompliancePage'))} />
          <Route path="/integrations-status" element={L(() => import('@/pages/dashboard/IntegrationsStatusPage'))} />
          <Route path="/profile" element={L(() => import('@/pages/dashboard/ProfilePage'))} />

          {/* Agentic Identity */}
          <Route path="/agents" element={L(() => import('@/pages/agents/AgentsPage'))} />
          <Route path="/agents/new" element={L(() => import('@/pages/agents/AgentNewPage'))} />
          <Route path="/agents/command-center" element={L(() => import('@/pages/agents/CommandCenterPage'))} />
          <Route path="/agents/anomalies" element={L(() => import('@/pages/agents/AnomaliesPage'))} />
          <Route path="/agents/policies" element={L(() => import('@/pages/agents/AgentPoliciesPage'))} />
          <Route path="/agents/policies/new" element={L(() => import('@/pages/agents/AgentPolicyNewPage'))} />
          <Route path="/agents/policies/:id" element={L(() => import('@/pages/agents/AgentPolicyDetailPage'))} />
          <Route path="/agents/approvals" element={L(() => import('@/pages/agents/ApprovalsPage'))} />
          <Route path="/agents/approvals/:id" element={L(() => import('@/pages/agents/ApprovalDetailPage'))} />
          <Route path="/agents/discovery" element={L(() => import('@/pages/agents/AgentDiscoveryPage'))} />
          <Route path="/agents/discovery/:id" element={L(() => import('@/pages/agents/AgentDiscoveryDetailPage'))} />
          <Route path="/agents/mcp" element={L(() => import('@/pages/agents/McpServersPage'))} />
          <Route path="/agents/mcp/:id" element={L(() => import('@/pages/agents/McpServerDetailPage'))} />
          <Route path="/agents/platforms" element={L(() => import('@/pages/agents/PlatformsPage'))} />
          <Route path="/agents/platforms/new" element={L(() => import('@/pages/agents/PlatformNewPage'))} />
          <Route path="/agents/platforms/:id" element={L(() => import('@/pages/agents/PlatformDetailPage'))} />
          <Route path="/agents/platforms/:id/offboard" element={L(() => import('@/pages/agents/PlatformOffboardPage'))} />
          <Route path="/agents/:id" element={L(() => import('@/pages/agents/AgentDetailPage'))} />

          {/* Settings */}
          <Route path="/settings" element={L(() => import('@/pages/dashboard/SettingsPage'))} />
          <Route path="/settings/security" element={L(() => import('@/pages/dashboard/SettingsSecurityPage'))} />
          <Route path="/settings/integrations" element={L(() => import('@/pages/dashboard/SettingsIntegrationsPage'))} />
          <Route path="/settings/notifications" element={L(() => import('@/pages/dashboard/SettingsNotificationsPage'))} />
          <Route path="/settings/ai-security" element={L(() => import('@/pages/dashboard/SettingsAiSecurityPage'))} />
          <Route path="/settings/ai-frameworks" element={L(() => import('@/pages/dashboard/SettingsAiFrameworksPage'))} />
        </Route>

        {/* Super Admin Portal */}
        <Route element={<AuthGuard requireSuperAdmin><MgtLayout /></AuthGuard>}>
          <Route path="/mgt" element={L(() => import('@/pages/mgt/MgtDashboardPage'))} />
          <Route path="/mgt/clients" element={L(() => import('@/pages/mgt/MgtClientsPage'))} />
          <Route path="/mgt/clients/new" element={L(() => import('@/pages/mgt/MgtClientNewPage'))} />
          <Route path="/mgt/clients/:id" element={L(() => import('@/pages/mgt/MgtClientDetailPage'))} />
          <Route path="/mgt/partners" element={L(() => import('@/pages/mgt/MgtPartnersPage'))} />
          <Route path="/mgt/partners/new" element={L(() => import('@/pages/mgt/MgtPartnerNewPage'))} />
          <Route path="/mgt/partners/:id" element={L(() => import('@/pages/mgt/MgtPartnerDetailPage'))} />
          <Route path="/mgt/ai-models" element={L(() => import('@/pages/mgt/MgtAiModelsPage'))} />
          <Route path="/mgt/ai-models/new" element={L(() => import('@/pages/mgt/MgtAiModelNewPage'))} />
          <Route path="/mgt/ai-models/:id" element={L(() => import('@/pages/mgt/MgtAiModelDetailPage'))} />
          <Route path="/mgt/plans" element={L(() => import('@/pages/mgt/MgtPlansPage'))} />
          <Route path="/mgt/security" element={L(() => import('@/pages/mgt/MgtSecurityPage'))} />
        </Route>

        {/* MSP Portal */}
        <Route element={<AuthGuard><MspLayout /></AuthGuard>}>
          <Route path="/msp" element={L(() => import('@/pages/msp/MspDashboardPage'))} />
          <Route path="/msp/clients" element={L(() => import('@/pages/msp/MspClientsPage'))} />
          <Route path="/msp/clients/new" element={L(() => import('@/pages/msp/MspAddClientPage'))} />
          <Route path="/msp/clients/:id" element={L(() => import('@/pages/msp/MspClientDetailPage'))} />
          <Route path="/msp/team" element={L(() => import('@/pages/msp/MspTeamPage'))} />
          <Route path="/msp/billing" element={L(() => import('@/pages/msp/MspBillingPage'))} />
          <Route path="/msp/settings" element={L(() => import('@/pages/msp/MspSettingsPage'))} />
          <Route path="/msp/support" element={L(() => import('@/pages/msp/MspSupportPage'))} />
        </Route>

        {/* Partner Portal */}
        <Route element={<AuthGuard><PartnerLayout /></AuthGuard>}>
          <Route path="/partner" element={L(() => import('@/pages/partner/PartnerDashboardPage'))} />
          <Route path="/partner/payouts" element={L(() => import('@/pages/partner/PartnerPayoutsPage'))} />
          <Route path="/partner/resources" element={L(() => import('@/pages/partner/PartnerResourcesPage'))} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
